import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { AuthContext } from '@/hooks/use-auth';
import type {
  UserSession,
  PortalType,
} from '@/types/auth';
import {
  cognitoSignIn,
  cognitoSignOut,
  getCurrentCognitoUser,
  completeNewPassword,
  type SignInResult,
  type CognitoUser,
} from '@/lib/cognito-client';

// Custom error class for auth errors
class AuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthError';
  }
}

interface AuthProviderProps {
  readonly children: ReactNode;
  readonly portalType: PortalType;
}

const isDev = import.meta.env.DEV;
const useMockAuth = import.meta.env.VITE_USE_MOCK_AUTH === 'true';

function mapCognitoUserToSession(cognitoUser: {
  userId: string;
  email: string;
  organizationId?: string;
  groups: string[];
  siteAccess: string[];
  venueAccess: string[];
  givenName?: string;
  familyName?: string;
  phoneNumber?: string;
  jobTitle?: string;
  preferredLanguage?: string;
  defaultSiteId?: string;
}): UserSession {
  return {
    userId: cognitoUser.userId,
    email: cognitoUser.email,
    organizationId: cognitoUser.organizationId ?? '',
    groups: cognitoUser.groups,
    siteAccess: cognitoUser.siteAccess,
    venueAccess: cognitoUser.venueAccess,
    givenName: cognitoUser.givenName,
    familyName: cognitoUser.familyName,
    phoneNumber: cognitoUser.phoneNumber,
    jobTitle: cognitoUser.jobTitle,
    preferredLanguage: cognitoUser.preferredLanguage,
    defaultSiteId: cognitoUser.defaultSiteId,
  };
}

export type AuthState = 
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | {
    status: 'authenticated';
    user: UserSession 
  }
  | {
    status: 'newPasswordRequired';
    username: string 
  }
  | {
    status: 'mfaRequired';
    challengeName: string 
  };

export function AuthProvider({
  children,
  portalType,
}: AuthProviderProps) {
  const [authState, setAuthState] = useState<AuthState>({ status: 'loading' });

  // Derived state for backward compatibility
  const user = authState.status === 'authenticated' ? authState.user : null;
  const isLoading = authState.status === 'loading';
  const isAuthenticated = authState.status === 'authenticated';
  const requiresNewPassword = authState.status === 'newPasswordRequired';
  const pendingUsername = authState.status === 'newPasswordRequired' ? authState.username : null;

  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (isDev && useMockAuth) {
          setAuthState({ status: 'unauthenticated' });
        } else {
          const cognitoUser = await getCurrentCognitoUser();
          if (cognitoUser) {
            setAuthState({ 
              status: 'authenticated', 
              user: mapCognitoUserToSession(cognitoUser) 
            });
          } else {
            setAuthState({ status: 'unauthenticated' });
          }
        }
      } catch {
        setAuthState({ status: 'unauthenticated' });
      }
    };

    void checkAuth();
  }, []);

  const signIn = useCallback(async (email: string, password: string): Promise<SignInResult> => {
    setAuthState({ status: 'loading' });
    try {
      if (isDev && useMockAuth) {
        if (!email || !password) {
          throw new AuthError('Email and password are required');
        }
        
        await new Promise((resolve) => setTimeout(resolve, 500));
        
        const mockUser: UserSession = {
          userId: 'mock-user-001',
          email,
          organizationId: 'org_demo',
          groups: ['admin'],
          siteAccess: [],
          venueAccess: [],
          givenName: 'Demo',
          familyName: 'User',
          jobTitle: 'Administrator',
          preferredLanguage: 'en',
        };
        
        setAuthState({
          status: 'authenticated',
          user: mockUser 
        });
        
        // Convert UserSession to CognitoUser for return type
        const cognitoUser: CognitoUser = {
          userId: mockUser.userId,
          email: mockUser.email,
          groups: mockUser.groups,
          siteAccess: mockUser.siteAccess,
          venueAccess: mockUser.venueAccess,
          givenName: mockUser.givenName,
          familyName: mockUser.familyName,
          jobTitle: mockUser.jobTitle,
          preferredLanguage: mockUser.preferredLanguage,
          organizationId: mockUser.organizationId,
        };
        
        return {
          status: 'success',
          user: cognitoUser,
        };
      }
      
      const result = await cognitoSignIn(email, password);
      
      switch (result.status) {
        case 'success':
          setAuthState({ 
            status: 'authenticated', 
            user: mapCognitoUserToSession(result.user) 
          });
          break;
          
        case 'newPasswordRequired':
          setAuthState({ 
            status: 'newPasswordRequired', 
            username: result.username 
          });
          break;
          
        case 'mfaRequired':
          setAuthState({ 
            status: 'mfaRequired', 
            challengeName: result.challengeName 
          });
          break;
      }
      
      return result;
    } catch (error) {
      setAuthState({ status: 'unauthenticated' });
      throw error;
    }
  }, []);

  const setNewPassword = useCallback(async (newPassword: string): Promise<void> => {
    if (authState.status !== 'newPasswordRequired') {
      throw new AuthError('Not in new password required state');
    }
    
    setAuthState({ status: 'loading' });
    try {
      const cognitoUser = await completeNewPassword(newPassword);
      setAuthState({ 
        status: 'authenticated', 
        user: mapCognitoUserToSession(cognitoUser) 
      });
    } catch (error) {
      setAuthState({
        status: 'newPasswordRequired',
        username: authState.username 
      });
      throw error;
    }
  }, [authState]);

  const signOut = useCallback(async () => {
    setAuthState({ status: 'loading' });
    try {
      if (!(isDev && useMockAuth)) {
        await cognitoSignOut();
      }
      setAuthState({ status: 'unauthenticated' });
    } catch {
      setAuthState({ status: 'unauthenticated' });
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      portalType,
      signIn,
      signOut,
      // New password flow
      requiresNewPassword,
      pendingUsername,
      setNewPassword,
    }),
    [user, isAuthenticated, isLoading, portalType, signIn, signOut, requiresNewPassword, pendingUsername, setNewPassword]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
